import { generatePath } from 'react-router-dom'
import { getResourceGlyph } from '@opallabs/gem'
import { OpalRoutes } from '@opallabs/routing'
import { SupportedResource, ResourceMeta } from '../types'

type ResourceMetaMap = {
  [K in SupportedResource]: ResourceMeta<K>
}

/**
 * Defines metadata for all resources supported by the modal.
 */
export const supportedResourceMeta: ResourceMetaMap = {
  content: {
    name: 'Content',
    glyph: (theme) => getResourceGlyph(theme, 'content', 'outlined'),
    getShareUrl: ({ workspace, momentId, legacyContentId }) => {
      return (
        window.location.origin +
        generatePath(
          // Strip digit regexs from route definition
          OpalRoutes.ContentShroud.replace(/\([d+\\]+(?=\))\)/g, ''),
          {
            workspaceSlug: workspace.slug,
            postId: legacyContentId ?? null,
            momentId: momentId ?? null,
          }
        )
      )
    },
  },
  moment: {
    name: 'Moment',
    glyph: (theme) => getResourceGlyph(theme, 'moment', 'outlined'),
    getShareUrl: ({ legacyMomentId, workspace }) => {
      // Strip digit regexes from route definition
      return (
        window.location.origin +
        generatePath(OpalRoutes.MomentShroud.replace(/\(.+\)/g, ''), {
          workspaceSlug: workspace.slug,
          momentId: legacyMomentId,
        })
      )
    },
  },
  story: {
    name: 'Story',
    glyph: (theme) => getResourceGlyph(theme, 'story', 'outlined'),
    getShareUrl: ({ legacyStoryId, workspace }) => {
      return (
        window.location.origin +
        generatePath(OpalRoutes.StoryCalendar.replace(/\(.+\)/g, ''), {
          workspaceSlug: workspace.slug,
          storyId: legacyStoryId,
        })
      )
    },
  },
  view: {
    name: 'Plan',
    glyph: (theme) => getResourceGlyph(theme, 'view'),
    getShareUrl: ({ resourceId, workspace, viewMode = 'edit' }) => {
      return (
        window.location.origin +
        generatePath(OpalRoutes.Plan, {
          workspaceSlug: workspace.slug,
          planId: resourceId,
          planViewMode: viewMode,
        })
      )
    },
  },
  block: {
    name: 'Block',
    glyph: (theme, displayedType) =>
      displayedType === 'Plan'
        ? getResourceGlyph(theme, 'view')
        : getResourceGlyph(theme, 'block', 'outlined'),
    getShareUrl: ({
      resourceId,
      workspace,
      planId,
      pairedMomentId,
      viewMode = 'edit',
      isRootBlock = false,
    }) => {
      if (pairedMomentId && planId) {
        return (
          window.location.origin +
          generatePath(OpalRoutes.MomentShroud.replace(/\(.+\)/g, ''), {
            workspaceSlug: workspace.slug,
            momentId: pairedMomentId,
          }) +
          `?returnTo=` +
          generatePath(OpalRoutes.Plan, {
            workspaceSlug: workspace.slug,
            planId: planId,
            planViewMode: viewMode,
          })
        )
      }
      if (isRootBlock) {
        // Standalone block w/o view is not yet supported
        if (!planId) return ''

        return (
          window.location.origin +
          generatePath(OpalRoutes.Plan, {
            workspaceSlug: workspace.slug,
            planId: planId,
            planViewMode: viewMode,
          })
        )
      }

      return (
        window.location.origin +
        generatePath(OpalRoutes.Block, {
          workspaceSlug: workspace.slug,
          planId: planId ?? null,
          planViewMode: viewMode,
          blockId: resourceId,
          blockViewMode: viewMode,
        })
      )
    },
  },
}
